import React from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { BRAND } from "@/config/brand";
import { Markdown } from "@/components/Markdown";
import { TERMS_MD } from "@/content/terms";
import { PRIVACY_MD } from "@/content/privacy";
import { GUIDELINES_MD } from "@/content/guidelines";
import { SAFETY_MD } from "@/content/safety";

export const CONSENT_VERSIONS = { terms: "2026-10-01", privacy: "2026-10-01", guidelines: "2026-10-01" };

const CONTENT = {
  "how-it-works": { title: "How It Works", body: [
    ["Two ways to talk", "Meet Someone Real matches you anonymously with another active adult. Meet an AI Persona opens a fresh, clearly-labelled fictional AI Mystery Match. The two modes are always kept distinct — we never swap an AI in for a human."],
    ["Anonymous & private", "Real matches show only a fresh alias, age band, gender and language. No names, photos, numbers or locations. AI messages disappear after 24 hours; ended anonymous chats vanish for both people."],
    ["Free preview", "Every new account gets one free human conversation (15 minutes or 30 messages) and 10 AI replies — no card needed."],
    ["Membership", "₹199 for 30 days, manually renewed. 30 new human matches/day, unlimited text with saved connections, 40 AI replies/day (1,000 per period), up to 10 AI personas."],
  ] },
  privacy: { title: "Privacy Policy", markdown: PRIVACY_MD },
  terms: { title: "Terms of Use", markdown: TERMS_MD },
  guidelines: { title: "Community Guidelines", markdown: GUIDELINES_MD },
  "ai-disclosure": { title: "AI Disclosure", body: [
    ["Personas are fictional AI", `Every AI persona on ${BRAND.name} is a fictional adult generated by software. They are clearly labelled "AI persona" on every screen and will truthfully confirm they are AI if asked.`],
    ["No real meetings", "An AI persona will never claim to be human, promise to meet you offline, or discourage real-world relationships."],
    ["Healthy design", "Personas are warm without encouraging dependency. They never use guilt, jealousy or pressure to keep you chatting or paying."],
    ["Your control", "AI messages auto-delete after 24 hours. Memories are only kept when you approve them, and you can edit or delete them anytime."],
  ] },
  refund: { title: "Refund & Cancellation Policy", body: [
    ["One-time membership", "Membership is ₹199 for 30 days. There is no automatic renewal, so nothing is charged again unless you choose to renew."],
    ["When we refund", "We refund duplicate charges, and cases where a payment succeeded but access could not be activated."],
    ["Free preview first", "Because a free preview lets you evaluate the service before paying, we do not offer broad change-of-mind refunds after access has been used."],
    ["How to request", `Email ${BRAND.supportEmail} with your order reference, or call us on +91 99789 88360 for any support.`],
  ] },
  "account-deletion": { title: "Account Deletion", body: [
    ["Delete anytime", "Open Settings and choose Delete account. This immediately removes your profile, connections, AI personas, approved memories and active chats."],
    ["What we must keep", "We retain only legally required payment records and any isolated moderation evidence until its scheduled expiry."],
    ["Sessions ended", "Deleting your account signs you out everywhere immediately."],
  ] },
  contact: { title: "Contact & Support", body: [
    ["Email us", `For help, safety concerns, refunds or account issues, email ${BRAND.supportEmail}.`],
    ["Reporting", "Inside a chat you can report or block instantly. Reports immediately end or separate the conversation."],
  ] },
  safety: { title: "Safety Centre", markdown: SAFETY_MD },
};

export default function Legal({ page }) {
  const c = CONTENT[page] || CONTENT.privacy;
  return (
    <div className="min-h-screen bg-[#0A0908] bg-noise px-5 py-8" data-testid={`legal-${page}`}>
      <div className="mx-auto max-w-2xl">
        <Link to="/" className="mb-6 inline-flex items-center gap-1 text-sm text-[#8C8578] hover:text-[#D4AF37]"><ArrowLeft className="h-4 w-4" /> Back</Link>
        {!c.markdown && <h1 className="font-display text-4xl font-medium tracking-tight text-[#F4F1EA]">{c.title}</h1>}
        {c.markdown ? (
          <div className="mt-6" data-testid="legal-markdown">
            <Markdown>{c.markdown}</Markdown>
          </div>
        ) : (
          <div className="mt-6 space-y-6">{c.body.map(([h, t]) => (<div key={h}><h2 className="font-display text-xl text-[#D4AF37]">{h}</h2><p className="mt-1 text-sm leading-relaxed text-[#C8C2B5]">{t}</p></div>))}</div>
        )}
      </div>
    </div>
  );
}
